import type { DbTagRow } from '../types.js';
import { defAtScopeAnchor, defVisibleInRefScopeChain, isModuleLevelDef, refScopeChain } from './scope-utils.js';

const OVERLOAD_KINDS = new Set(['function', 'method', 'type', 'interface', 'class']);

function declaredBeforeRef(def: DbTagRow, ref: DbTagRow): boolean {
  return def.start_line <= ref.start_line;
}

function resolveScopeLevelCandidates(defs: readonly DbTagRow[]): DbTagRow[] {
  if (defs.length <= 1) return [...defs];
  if (defs.every((def) => OVERLOAD_KINDS.has(def.kind))) return [...defs];

  const latestLine = Math.max(...defs.map((def) => def.start_line));
  const shadowed = defs.filter((def) => def.start_line === latestLine);
  return shadowed.length > 0 ? shadowed : [...defs];
}

/**
 * Resolve same-file definitions visible from a reference using the indexed scope chain.
 * Walks scopes innermost-first, then captured ancestors, then module-level bindings.
 */
export function resolveLocalDefinitions(ref: DbTagRow, fileDefs: readonly DbTagRow[]): DbTagRow[] {
  const named = fileDefs.filter((def) => def.name === ref.name && declaredBeforeRef(def, ref));
  if (named.length === 0) return [];

  const chain = refScopeChain(ref);

  if (chain.length > 0) {
    for (const anchor of chain) {
      const atLevel = named.filter((def) => defAtScopeAnchor(def, anchor));
      if (atLevel.length > 0) return resolveScopeLevelCandidates(atLevel);
    }

    const captured = named.filter((def) => defVisibleInRefScopeChain(def, ref));
    if (captured.length > 0) return resolveScopeLevelCandidates(captured);
  }

  const moduleLevel = named.filter((def) => isModuleLevelDef(def));
  if (moduleLevel.length > 0) return resolveScopeLevelCandidates(moduleLevel);

  if (named.length === 1) return [...named];

  return resolveScopeLevelCandidates(named);
}

/**
 * All same-file definitions that could plausibly bind the reference (for ambiguous pickers).
 */
export function resolveLocalDefinitionCandidates(ref: DbTagRow, fileDefs: readonly DbTagRow[]): DbTagRow[] {
  const named = fileDefs.filter((def) => def.name === ref.name && declaredBeforeRef(def, ref));
  if (named.length === 0) return [];

  const chain = refScopeChain(ref);
  if (chain.length === 0) return resolveScopeLevelCandidates(named.filter((def) => isModuleLevelDef(def)));

  const visible = named.filter((def) => {
    if (chain.some((anchor) => defAtScopeAnchor(def, anchor))) return true;
    if (defVisibleInRefScopeChain(def, ref)) return true;
    return isModuleLevelDef(def);
  });

  return visible.length > 0 ? visible : named;
}
